import {
  CHECKOUT_RESUME_MAX_AGE_MS,
  CHECKOUT_RESUME_MIN_AGE_MS,
} from "@/client/config/checkout-resume";
import type { UpgradeInterval, UpgradeTier } from "@/client/hooks/use-upgrade-checkout";

/**
 * The plan + billing cycle of the checkout the buyer most recently opened.
 *
 * Why this exists: a buyer who closes or abandons the Lemon Squeezy page lands
 * on `/billing/return?success=false`, a URL fixed in the Lemon Squeezy
 * dashboard that cannot carry per-buyer data. Without a memory of what they
 * were buying, the only way forward is a trip back through /pricing. This lets
 * the cancelled page reopen the SAME plan and cycle in one click.
 *
 * It only ever remembers a plan to OFFER again. Nothing here decides what a
 * checkout charges: the retry goes through the same `POST /billing/checkouts`
 * variant binding as every other upgrade control.
 */
export type CheckoutAttempt = { tier: UpgradeTier; interval: UpgradeInterval };

/** An attempt a later dashboard visit may offer back, with when it was made. */
export type ResumableCheckout = CheckoutAttempt & { at: number };

const STORAGE_KEY = "apply-edge:last-checkout-attempt";

/**
 * Fired on `window` whenever a checkout attempt is saved, so a dashboard that
 * stays mounted while the buyer opens a DIFFERENT checkout stops offering the
 * old plan instead of waiting for the next visit.
 */
export const CHECKOUT_ATTEMPT_SAVED_EVENT = "apply-edge:checkout-attempt-saved";

/** Which checkout attempt (by its `at`) the buyer dismissed on the dashboard card. */
const DISMISSED_KEY = "apply-edge:checkout-resume-dismissed";

/**
 * The attempt is written to BOTH stores. `sessionStorage` is per tab, so two
 * checkouts opened in two tabs each remember their own plan and cancelling one
 * cannot offer the other's; `localStorage` is the fallback for a cancelled
 * return that opens in a fresh tab (a redirect that lands elsewhere).
 */
const storages = (): Storage[] => {
  const found: Storage[] = [];
  for (const read of [() => globalThis.sessionStorage, () => globalThis.localStorage]) {
    try {
      // Absent (SSR, sandboxed frames) as well as blocked: both mean "no store".
      const store = read() as Storage | undefined;
      if (store) found.push(store);
    } catch {
      // Blocked storage is expected in private windows.
    }
  }
  return found;
};

/** A cancelled return is expected within minutes; a day-old attempt is stale intent. */
const ATTEMPT_TTL_MS = 6 * 60 * 60 * 1000;

const TIERS: readonly UpgradeTier[] = ["PRO", "ULTIMATE"];
const INTERVALS: readonly UpgradeInterval[] = ["weekly", "monthly", "yearly"];

export const parseCheckoutAttempt = (
  tier: string | null | undefined,
  interval: string | null | undefined,
): CheckoutAttempt | null => {
  const normalisedTier = tier?.toUpperCase();
  const normalisedInterval = interval?.toLowerCase();
  const validTier = TIERS.find((candidate) => candidate === normalisedTier);
  const validInterval = INTERVALS.find((candidate) => candidate === normalisedInterval);
  if (!validTier || !validInterval) return null;
  // Ultimate has no weekly variant; never offer a plan that does not exist.
  if (validTier === "ULTIMATE" && validInterval === "weekly") return null;
  return { tier: validTier, interval: validInterval };
};

/**
 * Best-effort: storage can be blocked (private window), and that must never break checkout.
 *
 * `userId` ties the attempt to the account that made it, so a dashboard visit by
 * a different account on the same browser is never offered someone else's
 * checkout. Anonymous buyers (pricing page before sign-in) pass none and are
 * never offered a resume.
 */
export const rememberCheckoutAttempt = (
  attempt: CheckoutAttempt,
  userId?: string,
  open?: { mode: "hosted" | "overlay"; opened: boolean },
): void => {
  const payload = JSON.stringify({
    ...attempt,
    ...(userId ? { userId } : {}),
    ...open,
    at: Date.now(),
  });
  for (const store of storages()) {
    try {
      store.setItem(STORAGE_KEY, payload);
    } catch {
      // Remembering is a convenience only.
    }
  }
  try {
    globalThis.dispatchEvent(new Event(CHECKOUT_ATTEMPT_SAVED_EVENT));
  } catch {
    // No window (SSR) means no mounted card to tell.
  }
};

const readAttempt = (store: Storage, now: number): CheckoutAttempt | null => {
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { tier?: string; interval?: string; at?: number };
    if (typeof parsed.at !== "number" || now - parsed.at > ATTEMPT_TTL_MS) return null;
    return parseCheckoutAttempt(parsed.tier, parsed.interval);
  } catch {
    return null;
  }
};

/** This tab's attempt wins; another tab's (shared localStorage) is only a fallback. */
export const recallCheckoutAttempt = (now: number = Date.now()): CheckoutAttempt | null => {
  for (const store of storages()) {
    const attempt = readAttempt(store, now);
    if (attempt) return attempt;
  }
  return null;
};

type StoredAttempt = {
  tier?: string;
  interval?: string;
  at?: number;
  userId?: string;
  mode?: string;
  opened?: boolean;
};

const readStored = (store: Storage): StoredAttempt | null => {
  try {
    const raw = store.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredAttempt) : null;
  } catch {
    return null;
  }
};

const readDismissedAt = (): number | null => {
  for (const store of storages()) {
    try {
      const raw = store.getItem(DISMISSED_KEY);
      const at = raw === null ? Number.NaN : Number(raw);
      if (Number.isFinite(at)) return at;
    } catch {
      // Unreadable means "not dismissed".
    }
  }
  return null;
};

/**
 * The checkout this account opened and walked away from, if the dashboard
 * should offer it back: made by `userId`, between the configured minimum and
 * maximum age, and not already dismissed. The most recent matching attempt
 * across this tab and the shared store wins.
 *
 * It only suggests a plan; the retry goes through the same variant-bound
 * `POST /billing/checkouts` as every other upgrade control.
 */
export const recallResumableCheckout = (
  userId: string,
  now: number = Date.now(),
): ResumableCheckout | null => {
  let best: ResumableCheckout | null = null;
  for (const store of storages()) {
    const stored = readStored(store);
    if (
      stored?.userId !== userId ||
      typeof stored.at !== "number" ||
      stored.mode !== "hosted" ||
      stored.opened !== true
    )
      continue;
    const age = now - stored.at;
    if (age < CHECKOUT_RESUME_MIN_AGE_MS || age > CHECKOUT_RESUME_MAX_AGE_MS) continue;
    const plan = parseCheckoutAttempt(stored.tier, stored.interval);
    if (!plan || (best && best.at >= stored.at)) continue;
    best = { ...plan, at: stored.at };
  }
  if (best && readDismissedAt() === best.at) return null;
  return best;
};

/** Milliseconds until `attempt` is too old to offer; 0 when it already is. */
export const msUntilCheckoutResumeExpires = (
  attempt: ResumableCheckout,
  now: number = Date.now(),
): number => Math.max(0, attempt.at + CHECKOUT_RESUME_MAX_AGE_MS - now);

/** Hide the resume card for this attempt; a newer checkout attempt shows it again. */
export const dismissCheckoutResume = (attempt: ResumableCheckout): void => {
  for (const store of storages()) {
    try {
      store.setItem(DISMISSED_KEY, String(attempt.at));
    } catch {
      // Dismissal is a convenience; the card just returns next visit.
    }
  }
  try {
    globalThis.dispatchEvent(new Event(CHECKOUT_ATTEMPT_SAVED_EVENT));
  } catch {
    // No mounted browser surface to refresh.
  }
};

/** A dismissal follows the attempt across mounts in this browser. */
export const isCheckoutResumeDismissed = (attempt: ResumableCheckout): boolean =>
  readDismissedAt() === attempt.at;
